import { FC, ReactNode, useEffect, useState } from "react";
import { FormattedMessage, IntlShape, useIntl } from "react-intl";

import { KeyboardArrowDown, KeyboardArrowRight } from "@mui/icons-material";
import { Box, Collapse, IconButton, Stack, Typography } from "@mui/material";

import { IShortTermPredictItem } from "../scheme/components/state-and-forecast/interfaces";
import {
  formatShortTermPredictText,
  getShortTermPredictBuildDate,
  getShortTermPredictValidUntilDate,
} from "../scheme/components/state-and-forecast/utils";
import { ALARMS, SemaforIcon } from "../../../../utils";
import { IRulPredictItem } from "./interfaces";
import { formatRulPredictPercent, hasRulPredictData } from "./utils";

const ForecastResultCard: FC<{ children: ReactNode }> = ({ children }) => (
  <Box
    sx={{
      px: 1,
      py: 0.75,
      borderRadius: 1,
      backgroundColor: "rgba(54, 56, 70, 0.06)",
    }}
  >
    {children}
  </Box>
);

const ShortTermForecastSection: FC<{
  formatDate: (date: Date | string) => string;
  isLoading?: boolean;
  items: IShortTermPredictItem[];
}> = ({ formatDate, isLoading = false, items }) => {
  const { formatMessage } = useIntl();

  return (
    <Box sx={{ mb: 1.5 }}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: { lg: 15, md: 13 },
          lineHeight: { lg: "20px", md: "18px" },
          color: "#363846",
          mb: 1,
        }}
      >
        <FormattedMessage id="pages.scheme.stateAndForecast.shortTermForecastTitle" />
      </Typography>
      {isLoading ? (
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472" }}>
          <FormattedMessage id="pages.forecasting.shortTermForecastLoading" />
        </Typography>
      ) : items.length === 0 ? (
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472" }}>
          <FormattedMessage id="pages.forecasting.shortTermForecastEmpty" />
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {items.map(item => {
            const buildDate = getShortTermPredictBuildDate(item);
            const validUntilDate = getShortTermPredictValidUntilDate(item);
            const hasValidDate = Number.isFinite(new Date(buildDate).getTime());
            const hasValidUntilDate = validUntilDate !== null && Number.isFinite(new Date(validUntilDate).getTime());
            const asOfLine = hasValidDate
              ? formatMessage(
                  { id: "pages.scheme.stateAndForecast.shortTermForecastAsOf" },
                  { date: formatDate(buildDate) },
                )
              : null;
            const validUntilLine =
              hasValidUntilDate && validUntilDate
                ? formatMessage(
                    { id: "pages.scheme.stateAndForecast.shortTermForecastValidUntil" },
                    { date: formatDate(validUntilDate) },
                  )
                : null;

            return (
              <ForecastResultCard key={`${item.idTrainMechanism}-${buildDate}`}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#363846", mb: 0.25 }}>
                  {item.mechanismName ||
                    formatMessage({ id: "pages.scheme.stateAndForecast.anomalyTooltip.sourceTypes.mechanism" })}
                </Typography>
                {asOfLine ? (
                  <Typography
                    sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472", mb: validUntilLine ? 0.25 : 0.5 }}
                  >
                    {asOfLine}
                  </Typography>
                ) : null}
                {validUntilLine ? (
                  <Typography sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472", mb: 0.5 }}>
                    {validUntilLine}
                  </Typography>
                ) : null}
                <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846", whiteSpace: "pre-wrap" }}>
                  {formatShortTermPredictText(item.mainResults)}
                </Typography>
                {item.additionalInfo ? (
                  <Typography
                    sx={{ fontSize: 11, lineHeight: "15px", color: "#5f6472", mt: 0.5, whiteSpace: "pre-wrap" }}
                  >
                    {formatShortTermPredictText(item.additionalInfo)}
                  </Typography>
                ) : null}
              </ForecastResultCard>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

const RulRiskLine: FC<{
  formatMessage: IntlShape["formatMessage"];
  formatNumber: IntlShape["formatNumber"];
  labelId: string;
  value: number | null;
}> = ({ formatMessage, formatNumber, labelId, value }) => {
  if (value === null) {
    return null;
  }

  return (
    <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846" }}>
      {formatMessage({ id: labelId })}: {formatRulPredictPercent(value, formatNumber)}
    </Typography>
  );
};

const RulForecastSection: FC<{
  formatDate: (date: Date | string) => string;
  formatNumber: IntlShape["formatNumber"];
  isLoading?: boolean;
  items: IRulPredictItem[];
}> = ({ formatDate, formatNumber, isLoading = false, items }) => {
  const { formatMessage } = useIntl();
  const visibleItems = items.filter(hasRulPredictData);

  return (
    <Box sx={{ mt: 1.5 }}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: { lg: 15, md: 13 },
          lineHeight: { lg: "20px", md: "18px" },
          color: "#363846",
          mb: 1,
        }}
      >
        <FormattedMessage id="pages.forecasting.rulForecastTitle" />
      </Typography>
      {isLoading ? (
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472" }}>
          <FormattedMessage id="pages.forecasting.rulForecastLoading" />
        </Typography>
      ) : visibleItems.length === 0 ? (
        <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#5f6472" }}>
          <FormattedMessage id="pages.forecasting.rulForecastEmpty" />
        </Typography>
      ) : (
        <Stack spacing={1.25}>
          {visibleItems.map(item => {
            const hasValidDate = Number.isFinite(new Date(item.date).getTime());
            const asOfLine = hasValidDate
              ? formatMessage({ id: "pages.forecasting.rulForecastAsOf" }, { date: formatDate(item.date) })
              : null;
            const statusLabel = item.alarmStatus !== null ? ALARMS[item.alarmStatus] ?? String(item.alarmStatus) : null;

            return (
              <ForecastResultCard key={`${item.idTrainMechanism}-${item.date}`}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: "#363846", mb: 0.25 }}>
                  {item.mechanismName ||
                    formatMessage({ id: "pages.scheme.stateAndForecast.anomalyTooltip.sourceTypes.mechanism" })}
                </Typography>
                {asOfLine ? (
                  <Typography sx={{ fontSize: 11, lineHeight: "14px", color: "#5f6472", mb: 0.5 }}>
                    {asOfLine}
                  </Typography>
                ) : null}
                {item.rulDays !== null ? (
                  <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846", mb: 0.25 }}>
                    {formatMessage({ id: "pages.forecasting.rulDays" }, { value: item.rulDays })}
                  </Typography>
                ) : null}
                {statusLabel ? (
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.25 }}>
                    <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846" }}>
                      {formatMessage({ id: "pages.forecasting.rulStatus" }, { status: statusLabel })}
                    </Typography>
                    {item.alarmStatus !== null ? (
                      <SemaforIcon status={item.alarmStatus} width={14} height={14} />
                    ) : null}
                  </Box>
                ) : null}
                {Object.values(item.risk).some(value => value !== null) ? (
                  <Box sx={{ mb: 0.25 }}>
                    <Typography sx={{ fontSize: 12, lineHeight: "16px", color: "#363846", mb: 0.25 }}>
                      <FormattedMessage id="pages.forecasting.rulRiskTitle" />
                    </Typography>
                    <RulRiskLine
                      formatMessage={formatMessage}
                      formatNumber={formatNumber}
                      labelId="pages.forecasting.rulRisk7d"
                      value={item.risk.risk7d}
                    />
                    <RulRiskLine
                      formatMessage={formatMessage}
                      formatNumber={formatNumber}
                      labelId="pages.forecasting.rulRisk30d"
                      value={item.risk.risk30d}
                    />
                    <RulRiskLine
                      formatMessage={formatMessage}
                      formatNumber={formatNumber}
                      labelId="pages.forecasting.rulRisk60d"
                      value={item.risk.risk60d}
                    />
                    <RulRiskLine
                      formatMessage={formatMessage}
                      formatNumber={formatNumber}
                      labelId="pages.forecasting.rulRisk90d"
                      value={item.risk.risk90d}
                    />
                  </Box>
                ) : null}
              </ForecastResultCard>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export const ForecastResultsPanel: FC<{
  formatDate: (date: Date | string) => string;
  formatNumber: IntlShape["formatNumber"];
  isRulLoading?: boolean;
  isShortTermLoading?: boolean;
  rulItems: IRulPredictItem[];
  shortTermItems: IShortTermPredictItem[];
}> = ({ formatDate, formatNumber, isRulLoading = false, isShortTermLoading = false, rulItems, shortTermItems }) => {
  const { formatMessage } = useIntl();
  const [expanded, setExpanded] = useState(false);
  const isLoading = isShortTermLoading || isRulLoading;
  const visibleRulItems = rulItems.filter(hasRulPredictData);
  const hasAnyContent = shortTermItems.length > 0 || visibleRulItems.length > 0;

  useEffect(() => {
    if (isLoading) {
      return;
    }

    setExpanded(hasAnyContent);
  }, [hasAnyContent, isLoading]);

  return (
    <Box
      sx={{
        mx: { xs: 1, md: 2 },
        mt: 1.25,
        mb: 0.75,
        border: "1px solid #d7dde9",
        borderRadius: 0.75,
        overflow: "hidden",
        backgroundColor: "#ffffff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 1,
          py: 0.75,
          backgroundColor: "#f5f7fb",
          borderBottom: expanded ? "1px solid #d7dde9" : "none",
        }}
      >
        <Typography sx={{ fontSize: 13, lineHeight: "16px", fontWeight: 600, color: "#363846" }}>
          <FormattedMessage id="pages.forecasting.forecastResultsTitle" />
        </Typography>
        <IconButton
          size="small"
          aria-expanded={expanded}
          aria-label={formatMessage({
            id: expanded ? "pages.forecasting.collapseForecastResults" : "pages.forecasting.expandForecastResults",
          })}
          onClick={() => setExpanded(current => !current)}
        >
          {expanded ? <KeyboardArrowDown fontSize="small" /> : <KeyboardArrowRight fontSize="small" />}
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit={false}>
        <Box sx={{ px: { xs: 1, md: 1.5 }, py: 1.25 }}>
          <ShortTermForecastSection formatDate={formatDate} isLoading={isShortTermLoading} items={shortTermItems} />
          <RulForecastSection
            formatDate={formatDate}
            formatNumber={formatNumber}
            isLoading={isRulLoading}
            items={rulItems}
          />
        </Box>
      </Collapse>
    </Box>
  );
};
